// Pick an existing record of a kind, or create one inline by typing a new name.
// Used anywhere a field points at another record (a project's lead, a deal's org,
// the org a showing went to).

import { useEffect, useRef, useState } from 'react'

import { newId } from '../lib/ids'
import { KINDS, titleField, type EntityKind } from '../lib/model'
import { entitiesOfKind } from '../lib/reducer'
import { useStore } from '../lib/store'
import { Button } from './common'
import { EntityLink } from './EntityLink'
import { Icon, iconName } from './Icon'

export function RefPicker({
  kind,
  value,
  placeholder,
  allowCreate = true,
  onPick,
  onClear,
}: {
  kind: EntityKind
  value: string
  placeholder?: string
  allowCreate?: boolean
  onPick: (id: string) => void
  onClear?: () => void
}) {
  const { state, dispatch } = useStore()
  const def = KINDS[kind]
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const wrap = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) {
        setOpen(false)
        setQ('')
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const ql = q.trim().toLowerCase()
  const all = entitiesOfKind(state, kind)
  const nameOf = (id: string) => String(state.entities[id]?.fields[titleField(kind)] ?? '')
  const exact = all.some((e) => nameOf(e.id).toLowerCase() === ql)
  const matches = all
    .filter((e) => ql === '' || nameOf(e.id).toLowerCase().includes(ql))
    .slice(0, 7)

  const pick = (id: string) => {
    onPick(id)
    setQ('')
    setOpen(false)
  }
  const createAndPick = () => {
    const name = q.trim()
    if (!name) return
    const id = newId(def.prefix)
    void dispatch({ type: `${kind}.create`, subject: id, data: { [titleField(kind)]: name } })
    pick(id)
  }

  if (value && !open) {
    return (
      <span className="inline-flex items-center gap-1">
        <EntityLink id={value} />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="opacity-40 hover:opacity-100"
          title={`Change ${def.singular.toLowerCase()}`}
        >
          <Icon name="Pencil" size={12} />
        </button>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="opacity-40 hover:opacity-100"
            title="Clear"
          >
            <Icon name="X" size={12} />
          </button>
        )}
      </span>
    )
  }

  if (!open) {
    return (
      <Button variant="quiet" size="dense" onClick={() => setOpen(true)}>
        <Icon name="Plus" size={12} /> {placeholder ?? `set ${def.singular.toLowerCase()}`}
      </Button>
    )
  }

  return (
    <span ref={wrap} className="relative inline-flex">
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false)
            setQ('')
          }
          if (e.key === 'Enter') {
            if (matches[0]) pick(matches[0].id)
            else if (allowCreate && q.trim() && !exact) createAndPick()
          }
        }}
        placeholder={`${def.singular}…`}
        className="rounded-control text-body w-44 border px-2 py-1 outline-none"
        style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
      />
      <ul
        className="rounded-control absolute top-full left-0 z-40 mt-1 w-56 overflow-auto border py-1 shadow-lg"
        style={{
          background: 'var(--panel)',
          borderColor: 'var(--border)',
          maxHeight: 240,
          overscrollBehavior: 'contain',
        }}
      >
        {matches.map((e) => (
          <li key={e.id}>
            <button
              type="button"
              onMouseDown={(ev) => {
                ev.preventDefault()
                pick(e.id)
              }}
              className="text-body flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[var(--hover)]"
            >
              <Icon name={iconName(def.icon)} size={14} style={{ color: 'var(--faint)' }} />
              <span className="truncate">{nameOf(e.id) || 'Untitled'}</span>
            </button>
          </li>
        ))}
        {allowCreate && q.trim() && !exact && (
          <li>
            <button
              type="button"
              onMouseDown={(ev) => {
                ev.preventDefault()
                createAndPick()
              }}
              className="text-body flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[var(--hover)]"
              style={{ color: 'var(--accent)' }}
            >
              <Icon name="Plus" size={14} /> Create “{q.trim()}”
            </button>
          </li>
        )}
        {matches.length === 0 && !q.trim() && (
          <li className="meta px-2.5 py-1.5">No {def.plural.toLowerCase()} yet. Type a name.</li>
        )}
      </ul>
    </span>
  )
}
